import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import { PageHeader } from "../../components/cards/PageHeader";
import { AppColors } from "../../constants/AppColors";
import { usePropertiesScreen } from "./hooks/usePropertiesScreen";
import AppLoadingOverlay from "../../components/loaders/AppLoadingOverlay";
import PropertyFormModal from "../../components/modals/PropertyFormModal";
import UnitFormModal from "../../components/modals/UnitFormModal";
import UpgradeRequiredModal from "../../components/modals/UpgradeRequiredModal";
import { PropertyResponse } from "../../models/ResponseModels/PropertyResponse";
import { UnitResponse } from "../../models/ResponseModels/UnitResponse";

const UNIT_ACCENTS = [
  "#14b8a6",
  "#8b5cf6",
  "#22c55e",
  "#f97316",
  "#2563eb",
  "#ec4899",
  "#06b6d4",
];

const formatPrice = (unit: UnitResponse) => {
  if (!unit.default_price_per_night) {
    return null;
  }

  return `${Number(unit.default_price_per_night).toFixed(2)}`;
};

const getPropertyLocation = (property: PropertyResponse) => {
  return [property.address, property.city].filter(Boolean).join(", ");
};

const copyExportLink = async (unit: UnitResponse, message: string) => {
  if (!unit.calendar_export_url) {
    return;
  }

  await navigator.clipboard.writeText(unit.calendar_export_url);
  window.alert(message);
};

const PropertiesScreen = () => {
  const vm = usePropertiesScreen();

  return (
    <Box>
      <AppLoadingOverlay visible={vm.loading} />

      <PageHeader
        title={vm.t("properties.title")}
        description={vm.t("properties.calendarSyncHelp")}
        action={
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: "wrap",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              rowGap: 1,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<HotelOutlinedIcon />}
              onClick={() => vm.openUnitModal()}
              disabled={vm.loading || vm.properties.length === 0}
            >
              {vm.t("properties.newUnit")}
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={vm.openPropertyModal}
              disabled={vm.loading}
            >
              {vm.t("properties.newProperty")}
            </Button>
          </Stack>
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <SummaryCard
          label={vm.t("properties.title")}
          value={vm.properties.length}
        />
        <SummaryCard label={vm.t("properties.units")} value={vm.units.length} />
        <SummaryCard
          label={vm.t("properties.activeUnits")}
          value={vm.activeUnits.length}
        />
        <SummaryCard
          label={vm.t("properties.inactiveUnits")}
          value={vm.inactiveUnits.length}
        />
      </Box>

      {vm.properties.length === 0 ? (
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography sx={{ fontWeight: 900 }}>
              {vm.t("properties.createProperty")}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {vm.t("properties.noUnitsDescription")}
            </Typography>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ mt: 2 }}
              onClick={vm.openPropertyModal}
              disabled={vm.loading}
            >
              {vm.t("properties.newProperty")}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Stack spacing={2}>
        {vm.properties.map((property, propertyIndex) => {
          const propertyUnits = vm.getUnitsForProperty(property.id);
          const activeUnits = vm.getActiveUnitsForProperty(property.id);
          const inactiveUnits = vm.getInactiveUnitsForProperty(property.id);

          return (
            <PropertyCard
              key={property.id}
              property={property}
              units={propertyUnits}
              activeUnitsCount={activeUnits.length}
              inactiveUnitsCount={inactiveUnits.length}
              propertyIndex={propertyIndex}
              loading={vm.loading}
              t={vm.t}
              onAddUnit={() => vm.openUnitModal(property)}
              onDeleteProperty={() => vm.confirmDeleteProperty(property)}
              onEditUnit={vm.openEditUnitModal}
              onDeleteUnit={vm.confirmDeleteUnit}
              onToggleUnitActive={vm.toggleUnitActive}
            />
          );
        })}
      </Stack>

      <PropertyFormModal
        open={vm.propertyModalOpen}
        loading={vm.loading}
        onClose={vm.closePropertyModal}
        onSubmit={vm.submitProperty}
      />

      <UnitFormModal
        open={vm.unitModalOpen}
        loading={vm.loading}
        properties={vm.properties}
        propertyId={vm.selectedProperty?.id || null}
        unit={vm.selectedUnit}
        onClose={vm.closeUnitModal}
        onSubmit={vm.submitUnit}
      />

      <UpgradeRequiredModal
        open={vm.upgradeModalOpen}
        title={vm.t("subscription.upgradeRequired")}
        message={vm.upgradeMessage}
        onClose={vm.closeUpgradeModal}
      />
    </Box>
  );
};

const PropertyCard = ({
  property,
  units,
  activeUnitsCount,
  inactiveUnitsCount,
  propertyIndex,
  loading,
  t,
  onAddUnit,
  onDeleteProperty,
  onEditUnit,
  onDeleteUnit,
  onToggleUnitActive,
}: {
  property: PropertyResponse;
  units: UnitResponse[];
  activeUnitsCount: number;
  inactiveUnitsCount: number;
  propertyIndex: number;
  loading: boolean;
  t: (key: string, values?: Record<string, string | number>) => string;
  onAddUnit: () => void;
  onDeleteProperty: () => void;
  onEditUnit: (unit: UnitResponse) => void;
  onDeleteUnit: (unit: UnitResponse) => void;
  onToggleUnitActive: (unit: UnitResponse) => void;
}) => {
  const location = getPropertyLocation(property);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${AppColors.border}`,
        bgcolor: AppColors.card,
        boxShadow: AppColors.shadowCard,
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2, sm: 3 },
          "&:last-child": {
            pb: { xs: 2, sm: 3 },
          },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "flex-start" },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 950,
                  lineHeight: 1.25,
                  wordBreak: "break-word",
                }}
              >
                {property.name}
              </Typography>

              <Chip
                size="small"
                label={`${units.length} ${t("properties.units")}`}
                sx={{
                  bgcolor: AppColors.surfaceSoft,
                  border: `1px solid ${AppColors.border}`,
                  fontWeight: 900,
                }}
              />
            </Stack>

            {location ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.35, wordBreak: "break-word" }}
              >
                {location}
              </Typography>
            ) : null}

            {property.notes ? (
              <Typography
                variant="body2"
                sx={{ mt: 1, color: AppColors.textSecondary }}
              >
                {property.notes}
              </Typography>
            ) : null}
          </Box>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: "wrap",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              rowGap: 1,
            }}
          >
            <Chip
              size="small"
              label={`${activeUnitsCount} ${t("properties.activeUnits")}`}
              color="primary"
              sx={{ fontWeight: 900 }}
            />

            <Chip
              size="small"
              label={`${inactiveUnitsCount} ${t("properties.inactiveUnits")}`}
              variant="outlined"
              sx={{ fontWeight: 900 }}
            />

            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onAddUnit}
              disabled={loading}
            >
              {t("properties.addUnit")}
            </Button>

            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              onClick={onDeleteProperty}
              disabled={loading}
            >
              {t("common.delete")}
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {units.length === 0 ? (
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: AppColors.surfaceBlue,
              border: `1px solid ${AppColors.border}`,
            }}
          >
            <Typography sx={{ fontWeight: 900 }}>
              {t("properties.noUnits")}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("properties.noUnitsDescription")}
            </Typography>

            <Button
              size="small"
              variant="contained"
              sx={{ mt: 1.5 }}
              onClick={onAddUnit}
              disabled={loading}
            >
              {t("properties.createUnit")}
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
                xl: "repeat(4, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            {units.map((unit, unitIndex) => (
              <UnitCard
                key={unit.id}
                unit={unit}
                accent={
                  UNIT_ACCENTS[
                    (propertyIndex + unitIndex) % UNIT_ACCENTS.length
                  ]
                }
                loading={loading}
                t={t}
                onEdit={() => onEditUnit(unit)}
                onDelete={() => onDeleteUnit(unit)}
                onToggleActive={() => onToggleUnitActive(unit)}
              />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const UnitCard = ({
  unit,
  accent,
  loading,
  t,
  onEdit,
  onDelete,
  onToggleActive,
}: {
  unit: UnitResponse;
  accent: string;
  loading: boolean;
  t: (key: string, values?: Record<string, string | number>) => string;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
}) => {
  const price = formatPrice(unit);
  const hasAirbnbSync = Boolean(unit.airbnb_ical_url);
  const hasBookingSync = Boolean(unit.booking_ical_url);
  const hasAnySync = hasAirbnbSync || hasBookingSync;

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${AppColors.border}`,
        bgcolor: unit.active ? AppColors.surface : AppColors.background,
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      <Box sx={{ height: 4, bgcolor: accent }} />

      <Box sx={{ p: 1.5 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "999px",
                  bgcolor: accent,
                  flexShrink: 0,
                }}
              />

              <Typography
                sx={{
                  fontWeight: 950,
                  lineHeight: 1.2,
                  wordBreak: "break-word",
                }}
              >
                {unit.name}
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.35, ml: 2 }}
            >
              {t(`properties.${unit.type}`)}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Tooltip
              title={
                unit.active
                  ? t("properties.deactivateUnit")
                  : t("properties.activateUnit")
              }
            >
              <IconButton
                size="small"
                onClick={onToggleActive}
                disabled={loading}
                sx={{ border: `1px solid ${AppColors.border}` }}
              >
                {unit.active ? (
                  <ToggleOnOutlinedIcon color="primary" fontSize="small" />
                ) : (
                  <ToggleOffOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title={t("common.edit")}>
              <IconButton
                size="small"
                onClick={onEdit}
                disabled={loading}
                sx={{ border: `1px solid ${AppColors.border}` }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("common.delete")}>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={loading}
                sx={{ border: `1px solid ${AppColors.border}` }}
              >
                <DeleteOutlineIcon color="error" fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: AppColors.background,
            border: `1px solid ${AppColors.border}`,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap", rowGap: 1 }}
          >
            <Chip
              size="small"
              label={`${t("properties.capacity")}: ${unit.capacity}`}
              sx={{
                fontWeight: 900,
                bgcolor: AppColors.card,
                border: `1px solid ${AppColors.border}`,
              }}
            />

            <Chip
              size="small"
              label={
                unit.active
                  ? t("properties.deactivateUnit")
                  : t("properties.activateUnit")
              }
              color={unit.active ? "success" : "default"}
              variant={unit.active ? "filled" : "outlined"}
              sx={{ fontWeight: 900 }}
            />
          </Stack>

          <Typography
            variant="body2"
            sx={{
              mt: 1,
              fontWeight: 900,
              color: AppColors.textPrimary,
            }}
          >
            {price || t("properties.noDefaultPrice")}
          </Typography>

          {unit.default_cleaner?.email ? (
            <Typography
              variant="body2"
              sx={{
                mt: 0.75,
                color: AppColors.textSecondary,
                fontWeight: 700,
                wordBreak: "break-word",
              }}
            >
              {unit.default_cleaner.email}
            </Typography>
          ) : null}
        </Box>

        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}
        >
          <Chip
            size="small"
            label={
              hasAirbnbSync
                ? t("properties.airbnbConnected")
                : t("properties.airbnbNotConnected")
            }
            color={hasAirbnbSync ? "primary" : "default"}
            variant={hasAirbnbSync ? "filled" : "outlined"}
            sx={{ fontWeight: 850 }}
          />

          <Chip
            size="small"
            label={
              hasBookingSync
                ? t("properties.bookingConnected")
                : t("properties.bookingNotConnected")
            }
            color={hasBookingSync ? "primary" : "default"}
            variant={hasBookingSync ? "filled" : "outlined"}
            sx={{ fontWeight: 850 }}
          />
        </Stack>

        {!hasAnySync ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, fontSize: 12.5, lineHeight: 1.35 }}
          >
            {t("properties.noExternalCalendarsConnected")}
          </Typography>
        ) : null}

        <Divider sx={{ my: 1.5 }} />

        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 800 }}
          >
            {t("properties.rentPilotExportCalendar")}
          </Typography>

          {unit.calendar_export_url ? (
            <Tooltip title={t("properties.copyShareExportLink")}>
              <IconButton
                size="small"
                onClick={() =>
                  copyExportLink(unit, t("properties.exportLinkCopied"))
                }
                disabled={loading}
                sx={{
                  border: `1px solid ${AppColors.border}`,
                  color: AppColors.primary,
                }}
              >
                <ContentCopyRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Chip
              size="small"
              label={t("common.none")}
              variant="outlined"
              sx={{ fontWeight: 900 }}
            />
          )}
        </Stack>
      </Box>
    </Box>
  );
};

const SummaryCard = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${AppColors.border}`,
        height: "100%",
      }}
    >
      <CardContent
        sx={{
          p: 2.5,
          "&:last-child": {
            pb: 2.5,
          },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 800 }}
        >
          {label}
        </Typography>

        <Typography variant="h5" sx={{ mt: 0.75, fontWeight: 950 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default PropertiesScreen;
